import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";

export function TeamsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const teams = useQuery({ queryKey: ["teams"], queryFn: api.listTeams });

  const create = useMutation({
    mutationFn: () => api.createTeam(name),
    onSuccess: async () => {
      setName("");
      await queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{t("teams.title")}</h1>
      <p className="mt-2 max-w-2xl text-sm text-zinc-400">{t("teams.intro")}</p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
        className="mt-6 flex flex-wrap items-center gap-2"
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t("teams.namePlaceholder")}
          maxLength={60}
          className="w-64 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-sky-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!name.trim() || create.isPending}
          className="rounded-lg bg-sky-500 px-3 py-1.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-sky-400 disabled:opacity-40"
        >
          {create.isPending ? t("teams.creating") : t("teams.create")}
        </button>
        {create.isError && (
          <span className="text-sm text-red-400">{create.error.message}</span>
        )}
      </form>

      {teams.isPending ? (
        <p className="mt-8 text-sm text-zinc-500">{t("teams.loading")}</p>
      ) : teams.isError ? (
        <p className="mt-8 text-sm text-red-400">{t("teams.error")}</p>
      ) : teams.data.length === 0 ? (
        <div className="mt-8 rounded-xl border border-zinc-800 px-6 py-16 text-center">
          <h2 className="text-lg font-medium">{t("teams.emptyTitle")}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
            {t("teams.emptyBody")}
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.data.map((team) => (
            <Link
              key={team.id}
              to={`/teams/${team.id}`}
              className="rounded-xl border border-zinc-800 px-4 py-4 transition-colors hover:border-zinc-600"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-zinc-100">{team.name}</span>
                {team.role === "owner" && (
                  <span className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-400">
                    {t("teams.owner")}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-zinc-500">
                {t("teams.drivers", { count: team.member_count })}
                {team.leaderboard_public && ` · ${t("teams.publicBoard")}`}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
